<script setup>
import i18next from 'i18next';
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { MarketOverview, TickerTape } from 'vue-tradingview-widgets';
import altcoins from '../../data/altcoins.json';
import forex from '../../data/forex.json';
import indizes from '../../data/indizes.json';
import rohstoffe from '../../data/symbols.json';
import { fetchData } from '../../fetchData';
import { formatPlainDateByRegion } from '../../helpers';
import { useCcm19Global } from '../../useCcm19Global';
import BreakingNews from './BreakingNews.vue';
import DetailAuthorBox from './DetailAuthorBox.vue';
import FinancialEducation from './FinancialEducation.vue';
import Highlights from './Highlights.vue';
import InlineAuthorBox from './InlineAuthorBox.vue';
import Intro from './Intro.vue';
import ProductCategory from './ProductCategory.vue';
import Sharing from './Sharing.vue';
import VideoList from './VideoList.vue';

const post = ref(null);
const route = useRoute();
const scrollPercent = ref(0);
const finanzBildungCategoryId = ref(null);

const fetchPost = async () => {
    try {
        const { result = {} } = await fetchData(`/api/v1/slugs/${route.params.id}`);
        post.value = result.data;

        updateRobotsMeta({ isAiGenerated: Boolean(post.value?.is_ai_generated) });
    } catch (e) {
        console.error('Error fetching post data:', e);
    }
};

const fetchFilters = async () => {
    try {
        const { result } = await fetchData('/api/v1/posts/filters');

        finanzBildungCategoryId.value = result.postCategories.data.find((cat) => cat.name === 'Finanzbildung')?.id;
    } catch (e) {
        console.error('Error fetching filter data:', e);
    }
};

const handleScroll = () => {
    const contentEl = document.getElementById('news-wrapper');

    if (contentEl) {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollHeight = Math.max(contentEl.scrollHeight - window.innerHeight, 1);
        let percent = (scrollTop / scrollHeight) * 100;
        percent = Math.max(0, Math.min(percent, 100));
        scrollPercent.value = percent;
    }
};

const updateRobotsMeta = ({ isAiGenerated = false, reset = false } = {}) => {
    const robotsMeta = document.querySelector('meta[name="robots"]');

    if (!robotsMeta) return;

    if (reset) {
        robotsMeta.setAttribute('content', 'index, follow');

        return;
    }

    robotsMeta.setAttribute('content', isAiGenerated ? 'noindex, nofollow' : 'index, follow');
};

onMounted(() => {
    fetchFilters();
    fetchPost();
    window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
    updateRobotsMeta({ reset: true });
    window.removeEventListener('scroll', handleScroll);
});

watch([route], fetchPost, { deep: true });
const options = {
    showSymbolLogo: true,
    colorTheme: 'light',
    isTransparent: false,
    displayMode: 'adaptive',
    locale: 'de'
};
const { onReady, tradingView } = useCcm19Global();
onReady(() => { });
</script>

<template>
    <div class="relative">
        <TickerTape v-if="tradingView" :options="options" />

        <div id="tradingroom-post" class="container mx-auto">
            <Intro class="my-4" />

            <div v-if="post" class="rounded-lg shadow-md p-8 relative h-[500px] z-10">
                <figure class="w-full h-full absolute top-0 left-0">
                    <div :style="`background-image: url(${post.image_lg ?? post.image})`"
                        class="bg-cover bg-no-repeat bg-center w-full h-full group-hover:scale-110 transition-all ease-in-out duration-500 rounded-lg">
                    </div>
                    <figcaption v-if="post.image_origin_source"
                        class="absolute top-4 right-4 bg-blue/60 backdrop-blur-md rounded-md text-xs px-1 p-1 group text-white z-50 flex items-center">
                        <span class="inline-flex font-medium gap-1 leading-none items-center">
                            <Icon type="InfoCircle" viewBox="0 0 20 20" width="15" height="15" class="fill-current" />
                            {{ $t('image_source', 'Bildquelle') }}
                        </span>

                        <span class="inline-flex items-center max-w-0 overflow-hidden
                                group-hover:max-w-[400px]
                                transition-[max-width] duration-300 ease-in-out leading-none">
                            <span>:</span>
                            <a :href="post.image_origin_source" class="ml-0.5 whitespace-nowrap leading-none"
                                rel="nofollow noopener" target="_blank">
                                {{ post.image_origin_source }}
                            </a>
                        </span>
                    </figcaption>
                </figure>
                <div
                    class="overlay-bottom-dark absolute top-0 bottom-0 left-0 right-0 w-full h-full z-[5] rounded-lg" />
                <div
                    class="absolute top-0 left-0 w-full h-full p-4 flex items-end text-white z-[10] group-hover:backdrop-blur-sm group-hover:bg-blue-dark/30">
                    <div class="w-full">
                        <div class="flex flex-col lg:flex-row lg:items-end lg:justify-between">
                            <h1 class="text-2xl font-bold">
                                {{ $translate(post.title) }}
                            </h1>

                            <small class="block z-[10] text-white shrink-0">
                                {{ formatPlainDateByRegion(post.published_at) }}
                            </small>
                        </div>

                        <hr class="border-t border-white/50 my-4" />

                        <div v-if="post.user.id" class="flex flex-col lg:flex-row lg:justify-between lg:items-center">
                            <router-link v-if="post.user" :to="`/hkcmnews/?authors=${post.user.id}`"
                                class="flex flex-row gap-2 items-center mb-4">
                                <img v-if="post.user.image" class="rounded-full w-12 h-12 object-cover object-center"
                                    :src="post.user.image" />
                                <div>
                                    <p>{{ $t('plain_from') }}: {{ post.user.name }}</p>
                                </div>
                            </router-link>

                            <div v-if="post.postCategories && post.postCategories.data.length" class="flex flex-row">
                                <div v-for="category in post.postCategories.data.slice(0, 5)" :key="category.id"
                                    class="bg-blue border border-blue text-white text-xs px-2 py-0.5 rounded-full">
                                    {{ $translate(category.name) }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else class="rounded-lg shadow-md p-8 relative h-[500px] z-10 animate-pulse bg-white">
                <div
                    class="overlay-bottom-dark absolute top-0 bottom-0 left-0 right-0 w-full h-full z-[5] rounded-lg" />
            </div>

            <div v-if="post && post?.authorbox" id="author-box" class="flex flex-col lg:flex-row gap-2 my-4 mb-8">
                <InlineAuthorBox :authorbox="post.authorbox" :user="post.user" />
            </div>

            <div id="news-wrapper" class="flex flex-col lg:flex-row gap-2 my-4 mb-8">
                <div class="w-full lg:w-2/3 my-4">
                    <div v-if="post">
                        <h1 v-if="!post.image" class="text-4xl font-bold mb-4">
                            {{ $translate(post.title) }}
                        </h1>

                        <div class="content fr-view max-w-4xl" v-html="$translate(post.content)" />

                        <div class="mt-8">
                            <h4>
                                {{ $t('plain_more_articles') }}
                            </h4>
                            <div class="mt-4">
                                <Highlights :except="[post.id]" :limit="3"
                                    :post_tags="post.postTags.data.map((item) => item.name)" />
                            </div>
                        </div>
                    </div>
                    <div v-else class="space-y-4">
                        <div class="h-6 bg-gray-200 rounded animate-pulse w-3/4"></div>
                        <div class="h-4 bg-gray-200 rounded animate-pulse w-1/2"></div>
                        <div class="h-4 bg-gray-200 rounded animate-pulse w-1/2"></div>
                    </div>
                </div>
                <div class="w-full lg:w-1/3 lg:pl-2 my-4 space-y-4">
                    <div class="border-2 border-blue p-2 h-[32.5rem] bg-white rounded-lg shadow-lg flex flex-col">
                        <div class="flex flex-row gap-2 justify-between items-center">
                            <strong> {{ $t('plain_breaking_news') }} </strong>
                            <router-link to="/hkcmnews/breaking-news"
                                class="inline-flex items-center gap-2 hover:text-blue hover:bg-gray-100 text-sm transition-all ease-in-out p-1 px-2 rounded-md cursor-pointer">
                                <span> {{ $t('plain_all') }} {{ $t('plain_breaking_news') }} </span>
                                <svg class="h-3" viewBox="0 0 12 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1.39685 3H10.2857H1.39685Z" fill="currentColor" />
                                    <path d="M1.39685 3H10.2857" stroke="currentColor" stroke-width="0.47619" />
                                    <path d="M7.42859 0.428589L10.2857 3.00002L7.42859 5.57145" stroke="currentColor"
                                        stroke-width="0.47619" stroke-linecap="square" />
                                </svg>
                            </router-link>
                        </div>
                        <BreakingNews />
                    </div>
                    <div v-if="finanzBildungCategoryId"
                        class="border-2 border-gray-300 p-2 h-[28rem] bg-white rounded-lg shadow-lg flex flex-col">
                        <div class="flex flex-row gap-2 justify-between items-center">
                            <strong> {{ $t('news_financial_education') }} </strong>
                            <router-link :to="`/hkcmnews?post_categories_id=${finanzBildungCategoryId}`"
                                class="inline-flex items-center gap-2 hover:text-blue hover:bg-gray-100 text-sm transition-all ease-in-out p-1 px-2 rounded-md cursor-pointer">
                                <span> {{ $t('plain_all') }} {{ $t('published_articles') }} </span>
                                <svg class="h-3" viewBox="0 0 12 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1.39685 3H10.2857H1.39685Z" fill="currentColor" />
                                    <path d="M1.39685 3H10.2857" stroke="currentColor" stroke-width="0.47619" />
                                    <path d="M7.42859 0.428589L10.2857 3.00002L7.42859 5.57145" stroke="currentColor"
                                        stroke-width="0.47619" stroke-linecap="square" />
                                </svg>
                            </router-link>
                        </div>
                        <FinancialEducation />
                    </div>
                    <div v-if="$getRegion() === 'de'"
                        class="border-2 border-gray-300 p-2 h-[28rem] bg-white rounded-lg shadow-lg flex flex-col">
                        <div class="flex flex-row gap-2 justify-between items-center">
                            <strong> HKCM Videos </strong>
                            <a href="https://www.youtube.com/@hkcm" target="_blank"
                                class="inline-flex items-center gap-2 hover:text-blue hover:bg-gray-100 text-sm transition-all ease-in-out p-1 px-2 rounded-md cursor-pointer">
                                <span> {{ $t('plain_all') }} Videos </span>
                                <svg class="h-3" viewBox="0 0 12 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1.39685 3H10.2857H1.39685Z" fill="currentColor" />
                                    <path d="M1.39685 3H10.2857" stroke="currentColor" stroke-width="0.47619" />
                                    <path d="M7.42859 0.428589L10.2857 3.00002L7.42859 5.57145" stroke="currentColor"
                                        stroke-width="0.47619" stroke-linecap="square" />
                                </svg>
                            </a>
                        </div>
                        <VideoList :params="{
                            'sort[by]': 'created_at',
                            'sort[order]': 'desc',
                            playlist_id: $getRegion() !== 'de' ? 'UUvLnTGJQthqju38QPtaD64Q' : 'UU3AdN1bEmEonuSwXNS8LixQ',
                            key: $getRegion() !== 'de' ? 'UUvLnTGJQthqju38QPtaD64Q' : 'UU3AdN1bEmEonuSwXNS8LixQ',
                            type: ['youtube']
                        }" />
                    </div>
                    <div v-if="$getRegion() === 'de'"
                        class="border-2 border-gray-300 p-2 h-[28rem] bg-white rounded-lg shadow-lg flex flex-col">
                        <div class="flex flex-row gap-2 justify-between items-center">
                            <strong> Phantom Videos </strong>
                            <a href="https://www.youtube.com/@phantombyhkcm" target="_blank"
                                class="inline-flex items-center gap-2 hover:text-blue hover:bg-gray-100 text-sm transition-all ease-in-out p-1 px-2 rounded-md cursor-pointer">
                                <span> {{ $t('plain_all') }} Videos </span>
                                <svg class="h-3" viewBox="0 0 12 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1.39685 3H10.2857H1.39685Z" fill="currentColor" />
                                    <path d="M1.39685 3H10.2857" stroke="currentColor" stroke-width="0.47619" />
                                    <path d="M7.42859 0.428589L10.2857 3.00002L7.42859 5.57145" stroke="currentColor"
                                        stroke-width="0.47619" stroke-linecap="square" />
                                </svg>
                            </a>
                        </div>
                        <VideoList :params="{
                            'sort[by]': 'created_at',
                            'sort[order]': 'desc',
                            playlist_id: 'PLsIDvtZfmyZQe9Nmi3sVxwhzn8151Cmpd',
                            key: 'PLsIDvtZfmyZQe9Nmi3sVxwhzn8151Cmpd',
                            type: ['youtube']
                        }" />
                    </div>
                    <div v-if="$getRegion() === 'de'"
                        class="border-2 border-gray-300 p-2 h-[28rem] bg-white rounded-lg shadow-lg flex flex-col">
                        <div class="flex flex-row gap-2 justify-between items-center">
                            <strong> Charts & Cars by Phillip Klinkmüller </strong>
                            <a href="https://www.youtube.com/@ChartsCarsbyPhilipKlinkm%C3%BCller" target="_blank"
                                class="inline-flex items-center gap-2 hover:text-blue hover:bg-gray-100 text-sm transition-all ease-in-out p-1 px-2 rounded-md cursor-pointer">
                                <span> {{ $t('plain_all') }} Videos </span>
                                <svg class="h-3" viewBox="0 0 12 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1.39685 3H10.2857H1.39685Z" fill="currentColor" />
                                    <path d="M1.39685 3H10.2857" stroke="currentColor" stroke-width="0.47619" />
                                    <path d="M7.42859 0.428589L10.2857 3.00002L7.42859 5.57145" stroke="currentColor"
                                        stroke-width="0.47619" stroke-linecap="square" />
                                </svg>
                            </a>
                        </div>
                        <VideoList :params="{
                            'sort[by]': 'created_at',
                            'sort[order]': 'desc',
                            playlist_id: 'PLpkkuYdgGRDlrtHT19AX5LSoVSUwLJlkJ',
                            key: 'PLpkkuYdgGRDlrtHT19AX5LSoVSUwLJlkJ',
                            type: ['youtube']
                        }" />
                    </div>
                    <div class="border-2 border-gray-300 p-2 bg-white rounded-lg shadow-lg flex flex-col">
                        <div class="flex flex-row gap-2 justify-between items-center">
                            <strong> {{ $t('news_global_markets') }} </strong>
                            <router-link to="/produkte"
                                class="inline-flex items-center gap-2 hover:text-blue hover:bg-gray-100 text-sm transition-all ease-in-out p-1 px-2 rounded-md cursor-pointer">
                                <span> {{ $t('news_our_products') }} </span>
                                <svg class="h-3" viewBox="0 0 12 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1.39685 3H10.2857H1.39685Z" fill="currentColor" />
                                    <path d="M1.39685 3H10.2857" stroke="currentColor" stroke-width="0.47619" />
                                    <path d="M7.42859 0.428589L10.2857 3.00002L7.42859 5.57145" stroke="currentColor"
                                        stroke-width="0.47619" stroke-linecap="square" />
                                </svg>
                            </router-link>
                        </div>

                        <div v-if="tradingView" class="relative">
                            <a href="https://www.tradingview.com/?aff_id=23069&source=NEWS" target="_blank"
                                rel="noopener noreferrer"
                                class="absolute top-2 right-2 text-[10px] bg-white/80 backdrop-blur-sm px-2 py-1 rounded-md shadow-sm hover:bg-white transition">
                                Try on TradingView →
                            </a>
                            <MarketOverview class="block mt-2 -m-2 rounded-xl" :options="{
                                tabs: [
                                    {
                                        title: i18next.t('plain_indices'),
                                        symbols: indizes,
                                        originalTitle: 'Indices'
                                    },
                                    {
                                        title: i18next.t('plain_commodities'),
                                        symbols: rohstoffe,
                                        originalTitle: 'Futures'
                                    },
                                    {
                                        title: 'Altcoins',
                                        symbols: altcoins,
                                        originalTitle: 'Bonds'
                                    },
                                    {
                                        title: 'Forex',
                                        symbols: forex,
                                        originalTitle: 'Forex'
                                    }
                                ],
                                showSymbolLogo: true,
                                colorTheme: 'light',
                                isTransparent: false,
                                displayMode: 'adaptive',
                                locale: 'en'
                            }" />
                            <p class="text-center text-gray-500 text-sm mt-2">
                                Powered by
                                <a href="https://www.tradingview.com/?aff_id=23069&source=NEWS" target="_blank"
                                    rel="noopener noreferrer" class="underline hover:text-blue-600 transition">
                                    TradingView
                                </a>
                            </p>
                        </div>
                        <div v-else
                            class="h-96 bg-gray-50 rounded-lg flex flex-col items-center justify-center p-4 text-center">
                            <CookieReset />
                        </div>
                    </div>
                    <div class="border-2 border-gray-300 p-2 bg-white rounded-lg shadow-lg flex flex-col">
                        <div class="flex flex-row gap-2 justify-between items-center">
                            <strong> {{ $t('news_our_products') }} </strong>
                            <router-link to="/produkte"
                                class="inline-flex items-center gap-2 hover:text-blue hover:bg-gray-100 text-sm transition-all ease-in-out p-1 px-2 rounded-md cursor-pointer">
                                <span> {{ $t('news_all_products') }} </span>
                                <svg class="h-3" viewBox="0 0 12 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1.39685 3H10.2857H1.39685Z" fill="currentColor" />
                                    <path d="M1.39685 3H10.2857" stroke="currentColor" stroke-width="0.47619" />
                                    <path d="M7.42859 0.428589L10.2857 3.00002L7.42859 5.57145" stroke="currentColor"
                                        stroke-width="0.47619" stroke-linecap="square" />
                                </svg>
                            </router-link>
                        </div>
                        <ProductCategory />
                    </div>
                    <div v-if="post && post?.authorbox"
                        class="border-2 border-gray-300 p-2 bg-white rounded-lg shadow-lg flex flex-col sticky top-24">
                        <div class="flex flex-row gap-2 justify-between items-center">
                            <strong> {{ $t('author_male') }} </strong>
                            <router-link to="/hkcmnews?authors="
                                class="inline-flex items-center gap-2 hover:text-blue hover:bg-gray-100 text-sm transition-all ease-in-out p-1 px-2 rounded-md cursor-pointer">
                                <span> {{ $t('plain_all') }} {{ $t('published_articles') }} </span>
                                <svg class="h-3" viewBox="0 0 12 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1.39685 3H10.2857H1.39685Z" fill="currentColor" />
                                    <path d="M1.39685 3H10.2857" stroke="currentColor" stroke-width="0.47619" />
                                    <path d="M7.42859 0.428589L10.2857 3.00002L7.42859 5.57145" stroke="currentColor"
                                        stroke-width="0.47619" stroke-linecap="square" />
                                </svg>
                            </router-link>
                        </div>
                        <DetailAuthorBox :authorbox="post.authorbox" :user="post.user" />
                    </div>
                </div>
            </div>
        </div>

        <div v-if="post"
            class="bg-blue-light/50 backdrop-blur-md sticky bottom-0 left-0 right-0 z-20 overflow-x-hidden">
            <div :style="{ width: `${scrollPercent}%` }" class="h-1 bg-blue"></div>
            <div class="container mx-auto py-2 flex flex-row justify-between items-center">
                <Sharing :title="$translate(post.title)" :url="`hkcmnews/${route.params.id}`" />
                <BackToTop class="shrink-0" />
            </div>
        </div>
    </div>
</template>
